import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getBillingContext } from "../../../api/client";
import { PLATFORM_CONSOLE_SEGMENT } from "./platformConsoleRoutes";

/** Ordem do menu (alinhada ao backend `PLATFORM_CONSOLE_SECTION_KEYS`). */
export const PLATFORM_CONSOLE_SECTION_ORDER = [
  PLATFORM_CONSOLE_SEGMENT.workspaces,
  PLATFORM_CONSOLE_SEGMENT.opportunities,
  PLATFORM_CONSOLE_SEGMENT.insights,
  PLATFORM_CONSOLE_SEGMENT.presence,
  PLATFORM_CONSOLE_SEGMENT.segments,
  PLATFORM_CONSOLE_SEGMENT.governance,
  PLATFORM_CONSOLE_SEGMENT.catalog,
];

const defaultSectionsAllTrue = () => {
  const o = {};
  PLATFORM_CONSOLE_SECTION_ORDER.forEach((k) => {
    o[k] = { view: true, create: true, edit: true, delete: true };
  });
  return o;
};

const PlatformConsoleAclContext = createContext({
  loading: true,
  superUser: false,
  sections: defaultSectionsAllTrue(),
  refresh: async () => {},
  can: () => true,
});

export function PlatformConsoleAclProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [superUser, setSuperUser] = useState(false);
  const [sections, setSections] = useState(() => defaultSectionsAllTrue());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ctx = await getBillingContext();
      const caps = ctx?.capabilities || {};
      const raw = caps.platform_console_sections;
      setSuperUser(Boolean(caps.platform_console_super));
      if (raw && typeof raw === "object") {
        const next = defaultSectionsAllTrue();
        PLATFORM_CONSOLE_SECTION_ORDER.forEach((k) => {
          const frag = raw[k];
          if (frag && typeof frag === "object") {
            next[k] = {
              view: Boolean(frag.view),
              create: Boolean(frag.create),
              edit: Boolean(frag.edit),
              delete: Boolean(frag.delete),
            };
          }
        });
        setSections(next);
      } else {
        setSections(defaultSectionsAllTrue());
      }
    } catch {
      setSections(defaultSectionsAllTrue());
      setSuperUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const can = useCallback(
    (section, action) => {
      if (superUser) return true;
      const s = sections?.[section];
      return Boolean(s?.[action]);
    },
    [sections, superUser]
  );

  const value = useMemo(
    () => ({
      loading,
      superUser,
      sections,
      refresh: load,
      can,
    }),
    [loading, superUser, sections, load, can]
  );

  return <PlatformConsoleAclContext.Provider value={value}>{children}</PlatformConsoleAclContext.Provider>;
}

export function usePlatformConsoleAcl() {
  return useContext(PlatformConsoleAclContext);
}
